import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  input,
  Input,
  Output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-scroll-area',
  imports: [CommonModule],
  templateUrl: './scroll-area.component.html',
  styleUrl: './scroll-area.component.scss',
})
export class ScrollAreaComponent implements AfterViewInit {
  /**
   * The maximum number of items to show.
   * @example 8
   */
  public thresholdItems = input<number>();

  public minHeight = input<number>(0);

  /**
   * Direct max-height in pixels. When provided, overrides thresholdItems * childItemHeight calculation.
   * Useful for lists with variable item heights.
   * @example 200
   */
  public maxHeight = input<number>();

  /**
   * The height in px of each item in the list.
   * @example 40
   */
  public childItemHeight = input<number>();
  /**
   * Defines the scroll direction:
   * 'vertical' | 'horizontal' | 'both'
   */
  @Input() scrollDirection: 'vertical' | 'horizontal' | 'both' = 'vertical';

  /**
   * Defines the scrolling behavior:
   * 'smooth' for smooth scrolling or 'auto' for instantaneous scrolling.
   */
  @Input() scrollBehavior: 'smooth' | 'auto' = 'auto';

  /**
   * The debounce time (in ms) for scroll events.
   */
  @Input() debounceTime: number = 200;

  /**
   * Defines the initial scroll position.
   * Example: { top: 50, left: 0 }
   */
  @Input() initialScrollPosition: { top?: number; left?: number } = { top: 0, left: 0 };

  /**
   * A flag to show or hide the border.
   * @default true
   */
  @Input() withBorder: boolean = true;

  /**
   * A flag to show or hide the divider.
   * @default true
   */
  @Input() withDivider: boolean = true;

  /**
   * Event fired whenever a scroll action occurs.
   * Emits an object with the positions { top, left }.
   */
  @Output() scrolled: EventEmitter<{ top: number; left: number }> = new EventEmitter();

  /**
   * Event fired when the user starts scrolling.
   */
  @Output() scrollStart: EventEmitter<void> = new EventEmitter();

  /**
   * Event fired when the scroll reaches the end.
   */
  @Output() scrollEnd: EventEmitter<void> = new EventEmitter();

  /**
   * @internal
   * Reference to the scroll container element.
   */
  public readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  protected _disabled = false;
  /**
   * Setters and getters for the disabled property.
   * Disables user scrolling.
   * When true, the container's overflow style is set to 'hidden'.
   */
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
  }

  scrollEffect = effect(() => {
    const element = this.scrollContainer().nativeElement;
    if (element  && this.initialScrollPosition) {
      if (this.initialScrollPosition.top !== undefined) {
        element.scrollTop = this.initialScrollPosition.top;
      }
      if (this.initialScrollPosition.left !== undefined) {
        element.scrollLeft = this.initialScrollPosition.left;
      }
    }
  })


  ngAfterViewInit(): void {
    this.enableScroll();
  }

  /**
   * Method called on the container's scroll event.
   */
  public onScroll(event: Event) {
    const element = event.target as HTMLElement;
    // Here you can implement debounce if necessary, using this.debounceTime
    this.scrolled.emit({ top: element.scrollTop, left: element.scrollLeft });

    // Example of detecting the start and end of a scroll:
    // These logics may vary according to the desired behavior.
    if (element.scrollTop === 0 && element.scrollLeft === 0) {
      this.scrollStart.emit();
    }
    if (
      element.scrollHeight - element.scrollTop === element.clientHeight ||
      element.scrollWidth - element.scrollLeft === element.clientWidth
    ) {
      this.scrollEnd.emit();
    }
  }

  /**
   * Allows programmatically scrolling to a specific position.
   */
  public scrollTo(position: { top?: number; left?: number }) {
    if (this.scrollContainer) {
      const element = this.scrollContainer().nativeElement;
      element.scrollTo({
        top: position.top ?? element.scrollTop,
        left: position.left ?? element.scrollLeft,
        behavior: this.scrollBehavior
      });
    }
  }

  /**
   * Resets the scroll position to the beginning.
   */
  public resetScroll() {
    this.scrollTo({ top: 0, left: 0 });
  }

  /**
   * Disables user scrolling by setting the container's overflow style to 'hidden'.
   */
  public disableScroll() {
    if (this.scrollContainer()) {
      const element = this.scrollContainer().nativeElement;
      element.style.overflowY = 'hidden';
      element.style.overflowX = 'hidden';
    }
  }

  /**
   * Enables user scrolling by restoring the container's overflow style based on scrollDirection.
   */
  public enableScroll() {
    if (this.scrollContainer) {
      const element = this.scrollContainer().nativeElement;
      switch (this.scrollDirection) {
        case 'vertical':
          element.style.overflowY = 'auto';
          element.style.overflowX = 'hidden';
          break;
        case 'horizontal':
          element.style.overflowX = 'auto';
          element.style.overflowY = 'hidden';
          break;
        case 'both':
          element.style.overflow = 'auto';
          break;
      }
    }
  }
}
